import { createContext, useContext, useMemo, useState } from 'react';
import { RuleChange, RuleCondition } from '../../../cashflow';
import { TransactionEntryRuleFieldsFragment } from '../../../graphql/generated';
import { TOASTS } from '../../../toasts';
import { isEmpty } from '../../../util/types';
import { CashflowFinancialEntity, CashflowTransactionEntry } from '../types';
import { useFinancialEntityMap } from '../useFinancialEntityMap';
import { RuleChangeWithEnabled, RuleConditionWithEnabled } from './config';
import useMatchingTransactions from './useMatchingTransactions';

type RuleView = 'form' | 'transactions';

type RuleContext = {
    rule?: TransactionEntryRuleFieldsFragment;
    conditions: RuleConditionWithEnabled[];
    enabledValidConditions: RuleCondition[];
    changes: RuleChangeWithEnabled[];
    enabledValidChanges: RuleChange[];
    isSubmitting: boolean;
    setIsSubmitting: (isSubmitting: boolean) => void;
    handleConditionChange: (condition: RuleConditionWithEnabled) => void;
    handleChangeChange: (change: RuleChangeWithEnabled) => void;
    applyToAll: boolean;
    setApplyToAll: (applyToAll: boolean) => void;
    matchingTransactions?: CashflowTransactionEntry[] | null;
    matchingTransactionsLoading: boolean;
    setView: (view: RuleView) => void;
    isFormValid: boolean;
    isGlobalRule: boolean;
    financialEntities: CashflowFinancialEntity[];
};

export const RuleContext = createContext<RuleContext>({
    conditions: [],
    changes: [],
    enabledValidChanges: [],
    enabledValidConditions: [],
    isSubmitting: false,
    setIsSubmitting: () => {},
    handleConditionChange: () => {},
    handleChangeChange: () => {},
    applyToAll: false,
    setApplyToAll: () => {},
    matchingTransactions: null,
    matchingTransactionsLoading: false,
    setView: () => {},
    isFormValid: false,
    rule: undefined,
    isGlobalRule: false,
    financialEntities: [],
});

export const useRuleContext = () => useContext(RuleContext);

export const useBuildRuleContext = ({
    conditions,
    changes,
    showErrorToast,
}: {
    conditions: RuleConditionWithEnabled[];
    changes: RuleChangeWithEnabled[];
    showErrorToast: (message: string) => void;
}) => {
    const [view, setView] = useState<RuleView>('form');

    const enabledValidConditions = useMemo(
        () =>
            conditions
                .filter(
                    (c) =>
                        c.enabled &&
                        (c.field === 'amount' && c.matcher === 'between'
                            ? c.value[0] < c.value[1]
                            : !isEmpty(c.value))
                )
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                .map(({ enabled, ...c }) => c),
        [conditions]
    );

    const enabledValidChanges = useMemo(
        () =>
            changes
                .filter((c) => c.enabled && !isEmpty(c.value))
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                .map(({ enabled, ...c }) => c),
        [changes]
    );

    const isFormValid =
        enabledValidConditions.length > 0 && enabledValidChanges.length > 0;

    const { matchingTransactions, loading: matchingTransactionsLoading } =
        useMatchingTransactions({
            conditions: enabledValidConditions,
            changes: enabledValidChanges,
            isFormValid,
            onError: () => {
                showErrorToast(TOASTS.findMatchingTransactionsError);
            },
        });

    const { financialEntityMap } = useFinancialEntityMap();

    const financialEntities = useMemo(
        () => Object.values(financialEntityMap ?? {}),
        [financialEntityMap]
    );

    return {
        view,
        setView,
        enabledValidConditions,
        enabledValidChanges,
        isFormValid,
        matchingTransactions,
        matchingTransactionsLoading,
        financialEntities,
    };
};
